/**
 * Utilities for syncing with Figma plugin
 * Handles export/import of primitives and tokens with ID mapping
 */

import { parseColor, getP3RgbFloats, formatP3Hex, formatOklch } from './colorUtils.js';

/**
 * Helper: Get folder path names for a folder
 */
function getFolderPathNames(folders, folder) {
  if (!folder) return [];
  const names = [];
  let current = folder;
  while (current && current.parentId) {
    const parent = (folders || []).find(f => f.id === current.parentId);
    if (!parent) break;
    names.unshift(parent.name);
    current = parent;
  }
  names.push(folder.name);
  return names;
}

function inferContrastAlgorithmFromCollections(collections = []) {
  const all = Array.isArray(collections) ? collections : [];
  const hasWcagKeyword = all.some((c) => {
    const name = String(c?.name || '').toLowerCase();
    const id = String(c?.id || '').toLowerCase();
    return name.includes('wcag') || id.includes('wcag');
  });
  if (hasWcagKeyword) return 'wcag';

  const contrastLevels = [];
  for (const collection of all) {
    const modes = Array.isArray(collection?.modes) ? collection.modes : [];
    for (const mode of modes) {
      const colors = Array.isArray(mode?.colors) ? mode.colors : [];
      for (const color of colors) {
        if (color?.mode === 'contrast' && typeof color?.contrastLevel === 'number' && isFinite(color.contrastLevel)) {
          contrastLevels.push(color.contrastLevel);
        }
      }
    }
  }
  if (contrastLevels.length === 0) return 'apca';
  return Math.max(...contrastLevels) <= 21 ? 'wcag' : 'apca';
}

/** Prefer explicit settings / data; only infer when both are absent. */
function resolveContrastAlgorithm(importData, fallback = 'apca') {
  const fromSettings = importData?.settings?.contrastAlgorithm;
  if (fromSettings === 'wcag' || fromSettings === 'apca') return fromSettings;
  const fromData = importData?.data?.contrastAlgorithm;
  if (fromData === 'wcag' || fromData === 'apca') return fromData;
  const inferred = inferContrastAlgorithmFromCollections(importData?.data?.collections || []);
  if (inferred === 'wcag' || inferred === 'apca') return inferred;
  return fallback === 'wcag' ? 'wcag' : 'apca';
}

/**
 * Helper: Get colors in visual order (unfoldered first, then folders recursively)
 * This matches the order they appear in the UI
 * @param {Array} colors - Array of color objects
 * @param {Array} folders - Array of folder objects
 * @returns {Array} Colors in visual order
 */
export function getColorsInVisualOrder(colors, folders) {
  const result = [];
  
  // Helper to get top-level folders
  const getTopLevelFolders = (folders) => (folders || []).filter(f => !f.parentId);
  
  // Helper to get child folders
  const getChildFolders = (folders, parentId) => (folders || []).filter(f => f.parentId === parentId);
  
  // Helper to get colors in a folder
  const getFolderedColors = (colors, folderId) => (colors || []).filter(c => c.folderId === folderId);
  
  // Helper to get unfoldered colors
  const getUnfolderedColors = (colors) => (colors || []).filter(c => !c.folderId);
  
  // Add unfoldered colors first
  const unfoldered = getUnfolderedColors(colors);
  result.push(...unfoldered);
  
  // Recursive function to add folder and its contents
  const addFolderRecursive = (folder) => {
    // Add colors in this folder
    const folderColors = getFolderedColors(colors, folder.id);
    result.push(...folderColors);
    
    // Add child folders recursively
    const childFolders = getChildFolders(folders, folder.id);
    childFolders.forEach(child => addFolderRecursive(child));
  };
  
  // Add top-level folders and their contents
  const topLevelFolders = getTopLevelFolders(folders);
  topLevelFolders.forEach(folder => addFolderRecursive(folder));
  
  return result;
}

/**
 * Export app data to format expected by Figma plugin
 * @param {Object} data - App state data
 * @param {Object} computedColors - Map of computed colors by ID from color engine (for primitives)
 * @param {Object} computedTokensByTheme - Map of theme ID to Map of computed token colors for that theme
 * @returns {Object} Export data in plugin format
 */
export function exportForFigmaPlugin({
  primitives,
  primitiveFolders,
  tokens,
  tokenFolders,
  colorThemes,
  primitivesSectionTitle = 'Primitives',
  themesSectionTitle = 'Themes',
  contrastAlgorithm = 'apca',
  colorSpace = 'p3'
}, computedColors = null, computedTokensByTheme = null) {
  // Compute a Display-P3 `{r, g, b}` payload for an exported color.
  //
  // Priority order:
  //   1. The engine's `computed` parsed color (preferred — already P3-clamped).
  //   2. Fall back to parsing the source string (`oklchValue` or `hexValue`).
  //   3. If both fail, return `null` and let the plugin fall back to hex.
  //
  // The plugin uses this when the destination Figma document has the
  // `DISPLAY_P3` color profile so it can write P3 channels directly instead
  // of going through `hexToRgb(hexValue)`, which sRGB-clamps wide-gamut chroma.
  const computeP3 = (computed, sourceColor) => {
    if (computed && computed.isValid) {
      const p3 = getP3RgbFloats(computed);
      if (p3) return p3;
    }
    if (sourceColor) {
      const input = sourceColor.oklchValue || sourceColor.colorValue || sourceColor.hexValue;
      if (typeof input === 'string' && input.trim()) {
        const parsed = parseColor(input, colorSpace);
        if (parsed && parsed.isValid) {
          const p3 = getP3RgbFloats(parsed);
          if (p3) return p3;
        }
      }
    }
    return null;
  };

  // Format P3 floats with enough precision for an 8-bit-stable round-trip.
  // 6 decimals gives sub-byte precision (1/255 ≈ 0.00392) so the encoded
  // bytes are stable across read/write cycles.
  const formatP3 = (p3) => {
    if (!p3) return null;
    const r = Math.round(p3.r * 1e6) / 1e6;
    const g = Math.round(p3.g * 1e6) / 1e6;
    const b = Math.round(p3.b * 1e6) / 1e6;
    const alpha = p3.alpha != null ? p3.alpha : 1;
    const out = { r, g, b };
    if (alpha < 1) {
      out.a = Math.round(alpha * 1e6) / 1e6;
    }
    return out;
  };

  const isP3Workspace = colorSpace === 'p3';

  /**
   * Display-P3 hex (`#RRGGBB` bytes = P3 gamma channels) for wide-gamut fidelity.
   * Only set when the app workspace is P3 — matches the card’s second label and
   * OkColor / Figma P3 variables. Kept separate from `hexValue`, which stays
   * **sRGB-clamped** for legacy tools and sRGB Figma documents.
   */
  const hexP3FromComputed = (computed, sourceColor) => {
    if (!isP3Workspace) return undefined;
    if (computed && computed.isValid) {
      const h = formatP3Hex(computed);
      return h || undefined;
    }
    const input = sourceColor?.oklchValue || sourceColor?.colorValue || sourceColor?.hexValue;
    if (typeof input === 'string' && input.trim()) {
      const parsed = parseColor(input, colorSpace);
      if (parsed?.isValid) {
        const h = formatP3Hex(parsed);
        return h || undefined;
      }
    }
    return undefined;
  };

  // Helper to get computed color value for primitives
  const getComputedPrimitiveColor = (color) => {
    if (computedColors && computedColors.has(color.id)) {
      const computed = computedColors.get(color.id);
      return {
        // sRGB 8-bit hex (parseColor) — safe for sRGB / LEGACY Figma docs & OKLCH round-trip
        hexValue: computed.hex || color.hexValue || color.colorValue,
        hexP3: hexP3FromComputed(computed, color),
        oklchValue: computed.oklch ? formatOklch(computed.oklch) : color.oklchValue,
        p3: formatP3(computeP3(computed, color))
      };
    }
    return {
      hexValue: color.hexValue || color.colorValue,
      hexP3: hexP3FromComputed(null, color),
      oklchValue: color.oklchValue,
      p3: formatP3(computeP3(null, color))
    };
  };
  
  // Helper to get computed color value for tokens (theme-specific)
  const getComputedTokenColor = (color, themeId) => {
    if (computedTokensByTheme && computedTokensByTheme[themeId]) {
      const themeComputed = computedTokensByTheme[themeId];
      if (themeComputed && themeComputed.has(color.id)) {
        const computed = themeComputed.get(color.id);
        return {
          hexValue: computed.hex || color.hexValue || color.colorValue,
          hexP3: hexP3FromComputed(computed, color),
          oklchValue: computed.oklch ? formatOklch(computed.oklch) : color.oklchValue,
          p3: formatP3(computeP3(computed, color))
        };
      }
    }
    return {
      hexValue: color.hexValue || color.colorValue,
      hexP3: hexP3FromComputed(null, color),
      oklchValue: color.oklchValue,
      p3: formatP3(computeP3(null, color))
    };
  };
  
  // Get primitives in visual order (unfoldered first, then folders)
  const primitivesInOrder = getColorsInVisualOrder(primitives || [], primitiveFolders || []);
  
  // Export primitives - don't include folder path in name, keep it separate
  const primitivesExport = primitivesInOrder.map(p => {
    const computed = getComputedPrimitiveColor(p);
    const mode = p.mode || 'static';
    
    // Base properties for all modes.
    // `p3` is the Display-P3 channel payload `{r, g, b}` in [0, 1] used by the
    // Figma plugin when the destination document has the DISPLAY_P3 color
    // profile. May be `null` if computation failed; the plugin will fall back
    // to `hexP3` then `hexValue`.
    // `hexP3` is Display-P3 hex (bytes = P3 channels); `hexValue` stays sRGB.
    const base = {
      id: p.id,
      name: p.name,  // Just the name, not the path
      mode: mode,
      hexValue: computed.hexValue,
      hexP3: computed.hexP3,
      oklchValue: computed.oklchValue,
      p3: computed.p3,
      folderId: p.folderId
    };
    
    // Add mode-specific properties
    if (mode === 'static') {
      return {
        ...base,
        colorValue: p.colorValue
      };
    } else if (mode === 'linked') {
      return {
        ...base,
        referenceColorId: p.referenceColorId || null,
        lExpression: p.lExpression || '',
        cExpression: p.cExpression || '',
        hExpression: p.hExpression || '',
        aExpression: p.aExpression || ''
      };
    } else if (mode === 'contrast') {
      return {
        ...base,
        backgroundColorId: p.backgroundColorId || null,
        foregroundColorId: p.foregroundColorId || null,
        contrastLevel: p.contrastLevel || null,
        useAlpha: p.useAlpha || false
      };
    }
    
    // Fallback for unknown modes
    return base;
  });

  // Export primitive folders
  const primitiveFoldersExport = (primitiveFolders || []).map(f => ({
    id: f.id,
    name: f.name,
    parentId: f.parentId || null
  }));

  // Build Primitives collection
  const primitivesCollection = {
    id: 'primitives',
    name: primitivesSectionTitle,
    modes: [
      {
        id: 'primitives-default',
        name: 'Mode 1',
        colors: primitivesExport,
        folders: primitiveFoldersExport
      }
    ]
  };

  // Build Themes collection with one mode per theme
  const tokenModes = (colorThemes || []).map(theme => {
    const themeFolders = tokenFolders[theme.id] || [];
    const themeTokensArray = tokens[theme.id] || [];
    
    // Get tokens in visual order (unfoldered first, then folders)
    const tokensInOrder = getColorsInVisualOrder(themeTokensArray, themeFolders);
    
    const themeTokens = tokensInOrder.map(t => {
      // Don't include folder path in name - keep it separate
      const computed = getComputedTokenColor(t, theme.id);
      const mode = t.mode || 'static';
      
      // Base properties for all modes. `p3` mirrors the primitive shape — see
      // the primitives export above for the field's purpose and semantics.
      const base = {
        id: t.id,
        name: t.name,  // Just the name, not the path
        mode: mode,
        hexValue: computed.hexValue,
        hexP3: computed.hexP3,
        oklchValue: computed.oklchValue,
        p3: computed.p3,
        folderId: t.folderId
      };
      
      // Add mode-specific properties
      if (mode === 'static') {
        return {
          ...base,
          colorValue: t.colorValue
        };
      } else if (mode === 'linked') {
        return {
          ...base,
          referenceColorId: t.referenceColorId || null,
          lExpression: t.lExpression || '',
          cExpression: t.cExpression || '',
          hExpression: t.hExpression || '',
          aExpression: t.aExpression || ''
        };
      } else if (mode === 'contrast') {
        return {
          ...base,
          backgroundColorId: t.backgroundColorId || null,
          foregroundColorId: t.foregroundColorId || null,
          contrastLevel: t.contrastLevel || null,
          useAlpha: t.useAlpha || false
        };
      }
      
      // Fallback for unknown modes
      return base;
    });

    const themeFoldersExport = themeFolders.map(f => ({
      id: f.id,
      name: f.name,
      parentId: f.parentId || null
    }));

    return {
      id: theme.id,
      name: theme.name,
      colors: themeTokens,
      folders: themeFoldersExport
    };
  });

  // Filter out empty modes (modes with no colors)
  const nonEmptyTokenModes = tokenModes.filter(mode => mode.colors && mode.colors.length > 0);
  
  const themesCollection = {
    id: 'themes',
    name: themesSectionTitle,
    modes: nonEmptyTokenModes
  };

  // Only include collections that have at least one color
  const collections = [];
  
  // Check if Primitives collection has any colors
  const hasPrimitives = primitivesExport && primitivesExport.length > 0;
  if (hasPrimitives) {
    collections.push(primitivesCollection);
  }
  
  // Check if Themes collection has any colors in any mode (after filtering empty modes)
  const hasThemes = nonEmptyTokenModes && nonEmptyTokenModes.length > 0;
  if (hasThemes) {
    collections.push(themesCollection);
  }

  // Return in unified format (v3.0)
  return {
    version: '3.0.0',
    settings: {
      contrastAlgorithm: contrastAlgorithm === 'wcag' ? 'wcag' : 'apca',
      colorSpace: colorSpace === 'p3' ? 'p3' : 'srgb'
    },
    data: {
      collections: collections
    }
  };
}

/**
 * Import data from Figma plugin format
 * Maps colors by ID to update existing colors, creates new ones if ID doesn't exist
 * @param {Object} importData - Data from Figma plugin (flexible format)
 * @param {Object} currentState - Current app state
 * @returns {Object} Updated state
 *
 * Static colors: prefer `oklchValue` over `colorValue` when OKLCH is present so
 * re-imported JSON never replaces the canonical OKLCH with a hex string (sRGB
 * or Display-P3 hex would both be wrong for `parseColor` unless interpreted).
 */
function staticColorValueFromImport(imported) {
  const ok = imported.oklchValue;
  if (typeof ok === 'string' && ok.trim()) return ok;
  if (imported.colorValue !== undefined) return imported.colorValue;
  return undefined;
}

export function importFromFigmaPlugin(importData, currentState) {
  const {
    primitives: currentPrimitives = [],
    primitiveFolders: currentPrimitiveFolders = [],
    tokens: currentTokens = {},
    tokenFolders: currentTokenFolders = {},
    colorThemes: currentThemes = [],
    primitivesSectionTitle: currentPrimitivesTitle = 'Primitives',
    themesSectionTitle: currentThemesTitle = 'Themes',
    contrastAlgorithm: currentContrastAlgorithm = 'apca',
    colorSpace: currentColorSpace = 'srgb'
  } = currentState;

  // Validate format
  if (!importData.data || !importData.data.collections || !Array.isArray(importData.data.collections)) {
    throw new Error('Invalid import format: data.collections must be an array');
  }

  const collections = importData.data.collections;

  // Find Primitives and Themes collections
  // Match by name ONLY (current app section title first, then fallback to default names)
  const primitivesCollection = collections.find(c =>
    c.name === currentPrimitivesTitle ||
    c.name === 'Primitives'
  );
  const themesCollection = collections.find(c =>
    c.name === currentThemesTitle ||
    c.name === 'Themes'
  );

  // Extract collection names (section titles)
  // If a collection was matched, use its name; otherwise keep the current app title
  const primitivesSectionTitle = primitivesCollection?.name || currentPrimitivesTitle;
  const themesSectionTitle = themesCollection?.name || currentThemesTitle;

  // Import primitives from first mode of primitives collection
  const primitivesMap = new Map(currentPrimitives.map(p => [p.id, p]));
  
  // Track export order for primitives (colorId -> index)
  const primitivesOrderMap = new Map();
  
  if (primitivesCollection && primitivesCollection.modes.length > 0) {
    const primitivesMode = primitivesCollection.modes[0];
    (primitivesMode.colors || []).forEach((importedPrimitive, index) => {
      // Track order
      primitivesOrderMap.set(importedPrimitive.id, index);
    const existing = primitivesMap.get(importedPrimitive.id);
    if (existing) {
      // Update existing primitive - preserve folderId, update values
      // Only update properties that exist in imported data (mode-specific)
      const update = {
        name: importedPrimitive.name, // Update name from Figma
        mode: importedPrimitive.mode,
        hexValue: importedPrimitive.hexValue,
        oklchValue: importedPrimitive.oklchValue
      };
      
      // Add mode-specific properties
      if (importedPrimitive.mode === 'static') {
        const cv = staticColorValueFromImport(importedPrimitive);
        if (cv !== undefined) update.colorValue = cv;
      } else if (importedPrimitive.mode === 'linked') {
        if (importedPrimitive.referenceColorId !== undefined) update.referenceColorId = importedPrimitive.referenceColorId;
        if (importedPrimitive.lExpression !== undefined) update.lExpression = importedPrimitive.lExpression;
        if (importedPrimitive.cExpression !== undefined) update.cExpression = importedPrimitive.cExpression;
        if (importedPrimitive.hExpression !== undefined) update.hExpression = importedPrimitive.hExpression;
        if (importedPrimitive.aExpression !== undefined) update.aExpression = importedPrimitive.aExpression;
      } else if (importedPrimitive.mode === 'contrast') {
        if (importedPrimitive.backgroundColorId !== undefined) update.backgroundColorId = importedPrimitive.backgroundColorId;
        if (importedPrimitive.foregroundColorId !== undefined) update.foregroundColorId = importedPrimitive.foregroundColorId;
        if (importedPrimitive.contrastLevel !== undefined) update.contrastLevel = importedPrimitive.contrastLevel;
        if (importedPrimitive.useAlpha !== undefined) update.useAlpha = importedPrimitive.useAlpha;
      }
      
      Object.assign(existing, update);
    } else {
      // Create new primitive - only include properties that exist
      const newPrimitive = {
        id: importedPrimitive.id,
        name: importedPrimitive.name,
        mode: importedPrimitive.mode,
        hexValue: importedPrimitive.hexValue,
        oklchValue: importedPrimitive.oklchValue,
        type: 'color',
        p3Support: true,
        isEditing: false,
        folderId: null // Will be set based on name path if needed
      };
      
      // Add mode-specific properties
      if (importedPrimitive.mode === 'static') {
        const cv = staticColorValueFromImport(importedPrimitive);
        if (cv !== undefined) newPrimitive.colorValue = cv;
      } else if (importedPrimitive.mode === 'linked') {
        if (importedPrimitive.referenceColorId !== undefined) newPrimitive.referenceColorId = importedPrimitive.referenceColorId;
        if (importedPrimitive.lExpression !== undefined) newPrimitive.lExpression = importedPrimitive.lExpression;
        if (importedPrimitive.cExpression !== undefined) newPrimitive.cExpression = importedPrimitive.cExpression;
        if (importedPrimitive.hExpression !== undefined) newPrimitive.hExpression = importedPrimitive.hExpression;
        if (importedPrimitive.aExpression !== undefined) newPrimitive.aExpression = importedPrimitive.aExpression;
      } else if (importedPrimitive.mode === 'contrast') {
        if (importedPrimitive.backgroundColorId !== undefined) newPrimitive.backgroundColorId = importedPrimitive.backgroundColorId;
        if (importedPrimitive.foregroundColorId !== undefined) newPrimitive.foregroundColorId = importedPrimitive.foregroundColorId;
        if (importedPrimitive.contrastLevel !== undefined) newPrimitive.contrastLevel = importedPrimitive.contrastLevel;
        if (importedPrimitive.useAlpha !== undefined) newPrimitive.useAlpha = importedPrimitive.useAlpha;
      }
      
      primitivesMap.set(importedPrimitive.id, newPrimitive);
    }
    });
  }

  // Reorder primitives to match export order
  // Colors not in export go to the end
  const maxPrimitiveOrder = primitivesOrderMap.size;
  const updatedPrimitives = Array.from(primitivesMap.values()).sort((a, b) => {
    const orderA = primitivesOrderMap.has(a.id) ? primitivesOrderMap.get(a.id) : maxPrimitiveOrder;
    const orderB = primitivesOrderMap.has(b.id) ? primitivesOrderMap.get(b.id) : maxPrimitiveOrder;
    return orderA - orderB;
  });

  // Import themes and tokens from themes collection
  const themesMap = new Map(currentThemes.map(t => [t.id, t]));
  const newThemes = [];
  const updatedTokens = { ...currentTokens };
  const updatedTokenFolders = { ...currentTokenFolders };
  const importedThemeIds = new Set(); // Track which themes we're importing
  
  // Track export order for tokens per theme (themeId -> Map<colorId, index>)
  const tokensOrderMaps = new Map();

  if (themesCollection && themesCollection.modes) {
    themesCollection.modes.forEach(modeData => {
      const themeName = modeData.name;
      
      // Match theme: by name first (supports Figma re-assigned IDs), then by ID
      let matchedTheme = Array.from(themesMap.values()).find(t => t.name === themeName);
      let themeId;
      
      if (matchedTheme) {
        // Matched by name — use the app's existing theme ID
        themeId = matchedTheme.id;
      } else if (themesMap.has(modeData.id)) {
        // Matched by ID — use the imported ID
        themeId = modeData.id;
        matchedTheme = themesMap.get(modeData.id);
      } else {
        // No match — this is a new theme, use the imported ID
        themeId = modeData.id;
      }
      
      // Track this theme as being imported
      importedThemeIds.add(themeId);
      
      // Ensure theme exists
      if (!matchedTheme) {
        const newTheme = { id: themeId, name: themeName };
        newThemes.push(newTheme);
        themesMap.set(themeId, newTheme);
      } else {
        // Update theme name
        matchedTheme.name = themeName;
      }

      // Initialize theme tokens if needed
      if (!updatedTokens[themeId]) {
        updatedTokens[themeId] = [];
      }

      // Map existing tokens by ID
      const tokensMap = new Map((updatedTokens[themeId] || []).map(t => [t.id, t]));

      // Track export order for tokens in this theme (colorId -> index)
      const tokensOrderMap = new Map();
      tokensOrderMaps.set(themeId, tokensOrderMap);

      // Import tokens for this theme
      (modeData.colors || []).forEach((importedToken, index) => {
        // Track order
        tokensOrderMap.set(importedToken.id, index);
      const existing = tokensMap.get(importedToken.id);
      if (existing) {
        // Update existing token - preserve folderId, update values
        // Only update properties that exist in imported data (mode-specific)
        const update = {
          name: importedToken.name, // Update name from Figma
          mode: importedToken.mode,
          hexValue: importedToken.hexValue,
          oklchValue: importedToken.oklchValue
        };
        
        // Add mode-specific properties
        if (importedToken.mode === 'static') {
          const cv = staticColorValueFromImport(importedToken);
          if (cv !== undefined) update.colorValue = cv;
        } else if (importedToken.mode === 'linked') {
          if (importedToken.referenceColorId !== undefined) update.referenceColorId = importedToken.referenceColorId;
          if (importedToken.lExpression !== undefined) update.lExpression = importedToken.lExpression;
          if (importedToken.cExpression !== undefined) update.cExpression = importedToken.cExpression;
          if (importedToken.hExpression !== undefined) update.hExpression = importedToken.hExpression;
          if (importedToken.aExpression !== undefined) update.aExpression = importedToken.aExpression;
        } else if (importedToken.mode === 'contrast') {
          if (importedToken.backgroundColorId !== undefined) update.backgroundColorId = importedToken.backgroundColorId;
          if (importedToken.foregroundColorId !== undefined) update.foregroundColorId = importedToken.foregroundColorId;
          if (importedToken.contrastLevel !== undefined) update.contrastLevel = importedToken.contrastLevel;
          if (importedToken.useAlpha !== undefined) update.useAlpha = importedToken.useAlpha;
        }
        
        Object.assign(existing, update);
      } else {
        // Create new token - only include properties that exist
        const newToken = {
          id: importedToken.id,
          name: importedToken.name,
          mode: importedToken.mode,
          hexValue: importedToken.hexValue,
          oklchValue: importedToken.oklchValue,
          type: 'color',
          p3Support: true,
          isEditing: false,
          folderId: null // Will be set based on name path if needed
        };
        
        // Add mode-specific properties
        if (importedToken.mode === 'static') {
          const cv = staticColorValueFromImport(importedToken);
          if (cv !== undefined) newToken.colorValue = cv;
        } else if (importedToken.mode === 'linked') {
          if (importedToken.referenceColorId !== undefined) newToken.referenceColorId = importedToken.referenceColorId;
          if (importedToken.lExpression !== undefined) newToken.lExpression = importedToken.lExpression;
          if (importedToken.cExpression !== undefined) newToken.cExpression = importedToken.cExpression;
          if (importedToken.hExpression !== undefined) newToken.hExpression = importedToken.hExpression;
          if (importedToken.aExpression !== undefined) newToken.aExpression = importedToken.aExpression;
        } else if (importedToken.mode === 'contrast') {
          if (importedToken.backgroundColorId !== undefined) newToken.backgroundColorId = importedToken.backgroundColorId;
          if (importedToken.foregroundColorId !== undefined) newToken.foregroundColorId = importedToken.foregroundColorId;
          if (importedToken.contrastLevel !== undefined) newToken.contrastLevel = importedToken.contrastLevel;
          if (importedToken.useAlpha !== undefined) newToken.useAlpha = importedToken.useAlpha;
        }
        
        tokensMap.set(importedToken.id, newToken);
      }
      });

      // Reorder tokens to match export order for this theme
      // Colors not in export go to the end
      const themeTokensOrderMap = tokensOrderMaps.get(themeId) || new Map();
      const maxTokenOrder = themeTokensOrderMap.size;
      updatedTokens[themeId] = Array.from(tokensMap.values()).sort((a, b) => {
        const orderA = themeTokensOrderMap.has(a.id) ? themeTokensOrderMap.get(a.id) : maxTokenOrder;
        const orderB = themeTokensOrderMap.has(b.id) ? themeTokensOrderMap.get(b.id) : maxTokenOrder;
        return orderA - orderB;
      });

      // Import token folders for this theme (preserve existing structure)
      // Note: Figma doesn't support folders, so we preserve app folder structure
      if (!updatedTokenFolders[themeId]) {
        updatedTokenFolders[themeId] = currentTokenFolders[themeId] || [];
      }
    });
  }

  let updatedThemes = [...Array.from(themesMap.values()), ...newThemes];
  
  // If we imported themes from Figma, remove the Default theme if:
  // 1. It wasn't in the import
  // 2. It has no tokens
  if (themesCollection && themesCollection.modes && themesCollection.modes.length > 0) {
    updatedThemes = updatedThemes.filter(theme => {
      // Keep theme if it was in the import
      if (importedThemeIds.has(theme.id)) return true;
      
      // Remove Default theme if it's empty
      if (theme.id === 'default' && theme.name === 'Default') {
        const hasTokens = updatedTokens[theme.id] && updatedTokens[theme.id].length > 0;
        if (!hasTokens) {
          // Also remove from tokens and tokenFolders objects
          delete updatedTokens['default'];
          delete updatedTokenFolders['default'];
        }
        return hasTokens; // Only keep if it has tokens
      }
      
      // Keep all other themes
      return true;
    });
  }

  // Color space is currently authoritative from settings (no heuristic inference).
  // Fall back to the importer's current workspace setting if the file is silent.
  const importedColorSpaceRaw =
    importData?.settings?.colorSpace ?? importData?.data?.colorSpace ?? null;
  const importedColorSpace =
    importedColorSpaceRaw === 'p3' || importedColorSpaceRaw === 'display-p3' || importedColorSpaceRaw === 'displayP3'
      ? 'p3'
      : (importedColorSpaceRaw === 'srgb' ? 'srgb' : (currentColorSpace === 'p3' ? 'p3' : 'srgb'));
  return {
    primitives: updatedPrimitives,
    primitiveFolders: currentPrimitiveFolders, // Preserve folder structure
    tokens: updatedTokens,
    tokenFolders: updatedTokenFolders, // Preserve folder structure
    colorThemes: updatedThemes,
    primitivesSectionTitle,
    themesSectionTitle,
    contrastAlgorithm: resolveContrastAlgorithm(
      importData,
      currentContrastAlgorithm === 'wcag' ? 'wcag' : 'apca'
    ),
    colorSpace: importedColorSpace
  };
}

/**
 * Download JSON file
 */
export function downloadJsonFile(data, filename) {
  const jsonString = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Read JSON file from file input
 */
export function readJsonFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result;
        const data = JSON.parse(text);
        resolve(data);
      } catch (error) {
        reject(new Error('Invalid JSON file: ' + error.message));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

